/**
 * ExcelStyledTable — Interactive Spreadsheet Renderer
 *
 * Renders an Excel-like table in the browser with:
 *  - Column resizing (drag column borders)
 *  - Double-click to auto-fit column width
 *  - Merged cell support (colSpan / rowSpan)
 *  - Background fills, font styling, borders, alignment
 *  - Proper text wrapping (never breaks individual characters)
 *  - Click-to-select, double-click-to-edit cells
 *  - Tab / Enter / Escape keyboard navigation while editing
 *  - Zoom controls
 *  - Sticky row & column headers
 *  - Tooltip on hover (shows full cell content)
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Box, IconButton, InputBase, Stack, Tooltip, Typography } from '@mui/material';
import { ZoomIn as IconZoomIn, ZoomOut as IconZoomOut } from '@mui/icons-material';

import type { StyledRow, CellStyle, BorderStyle, SheetMetadata } from './parseExcelStyled';

// Props

interface ExcelStyledTableProps {
	rows: StyledRow[];
	columns: string[];
	metadata: SheetMetadata;
	headerLabels?: Record<string, string>;
	editable?: boolean;
	onCellChange?: (rowIndex: number, col: string, newValue: string) => void;
	maxHeight?: number | string;
}

// Border → CSS

function cssBorder(b: BorderStyle | undefined): string {
	if (!b || !b.style) return 'none';
	const c = b.color || '#000';
	switch (b.style) {
		case 'thin':
			return `1px solid ${c}`;
		case 'medium':
			return `2px solid ${c}`;
		case 'thick':
			return `3px solid ${c}`;
		case 'dashed':
			return `1px dashed ${c}`;
		case 'dotted':
			return `1px dotted ${c}`;
		case 'double':
			return `3px double ${c}`;
		case 'hair':
			return `1px solid ${c}`;
		case 'mediumDashed':
			return `2px dashed ${c}`;
		case 'dashDot':
			return `1px dashed ${c}`;
		case 'mediumDashDot':
			return `2px dashed ${c}`;
		case 'dashDotDot':
			return `1px dotted ${c}`;
		case 'mediumDashDotDot':
			return `2px dotted ${c}`;
		case 'slantDashDot':
			return `2px dashed ${c}`;
		default:
			return `1px solid ${c}`;
	}
}

// CellStyle → CSS

function cellStyleToCSS(style?: CellStyle): CSSProperties {
	if (!style) return {};
	const css: CSSProperties = {};

	// Background
	if (style.fill) css.backgroundColor = style.fill;

	// Font
	if (style.bold) css.fontWeight = 700;
	if (style.italic) css.fontStyle = 'italic';
	if (style.underline) css.textDecoration = 'underline';
	if (style.strike)
		css.textDecoration = css.textDecoration ? `${css.textDecoration} line-through` : 'line-through';
	if (style.color) css.color = style.color;
	if (style.fontSize) css.fontSize = `${style.fontSize}pt`;
	if (style.fontFamily) css.fontFamily = style.fontFamily;

	// Alignment
	if (style.align) css.textAlign = style.align as CSSProperties['textAlign'];
	if (style.verticalAlign) css.verticalAlign = style.verticalAlign;

	// Text wrapping — CRITICAL: never use word-break which breaks individual chars
	if (style.wrapText) {
		css.whiteSpace = 'pre-wrap';
		css.overflowWrap = 'break-word'; // wrap at word boundaries, break long words
		css.wordBreak = 'normal'; // do NOT break mid-character
	} else {
		css.whiteSpace = 'nowrap';
		css.overflow = 'hidden';
		css.textOverflow = 'ellipsis';
	}

	if (style.indent) css.paddingLeft = `${style.indent * 12}px`;
	if (style.rotation) css.transform = `rotate(-${style.rotation}deg)`;

	// Borders
	if (style.borders) {
		if (style.borders.top) css.borderTop = cssBorder(style.borders.top);
		if (style.borders.bottom) css.borderBottom = cssBorder(style.borders.bottom);
		if (style.borders.left) css.borderLeft = cssBorder(style.borders.left);
		if (style.borders.right) css.borderRight = cssBorder(style.borders.right);
	}

	return css;
}

// Dark color detection (for auto-contrast text)

function isDarkColor(hex?: string): boolean {
	if (!hex) return false;
	const h = hex.replace('#', '');
	if (h.length < 6) return false;
	const r = parseInt(h.substring(0, 2), 16);
	const g = parseInt(h.substring(2, 4), 16);
	const b = parseInt(h.substring(4, 6), 16);
	return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

// Inline Editing Cell

const EditableCell = ({
	value,
	style,
	onSave,
	onCancel,
	onTab,
}: {
	value: string;
	style: CSSProperties;
	onSave: (val: string) => void;
	onCancel: () => void;
	onTab: (shiftKey: boolean) => void;
}) => {
	const [editValue, setEditValue] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, []);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onSave(editValue);
		} else if (e.key === 'Escape') {
			onCancel();
		} else if (e.key === 'Tab') {
			e.preventDefault();
			onSave(editValue);
			onTab(e.shiftKey);
		}
	};

	return (
		<InputBase
			inputRef={inputRef}
			value={editValue}
			onChange={(e) => setEditValue(e.target.value)}
			onKeyDown={handleKeyDown}
			onBlur={() => onSave(editValue)}
			size="small"
			sx={{
				width: '100%',
				height: '100%',
				p: 0,
				'& input': {
					p: '2px 4px',
					fontSize: style.fontSize || '10pt',
					fontFamily: style.fontFamily || 'Calibri, Arial, sans-serif',
					fontWeight: style.fontWeight || 400,
					fontStyle: style.fontStyle || 'normal',
					color: '#000',
					background: 'rgba(79, 195, 247, 0.12)',
					border: '2px solid #1976d2',
					borderRadius: '2px',
					outline: 'none',
				},
			}}
		/>
	);
};

// Main Component

export const ExcelStyledTable = ({
	rows,
	columns,
	metadata,
	headerLabels,
	editable = false,
	onCellChange,
	maxHeight = 700,
}: ExcelStyledTableProps) => {
	// --- State: editing -------------------------------------------------
	const [editingCell, setEditingCell] = useState<{ rowIndex: number; col: string } | null>(null);
	const [selectedCell, setSelectedCell] = useState<{ rowIndex: number; col: string } | null>(null);

	// --- State: zoom ----------------------------------------------------
	const [zoom, setZoom] = useState(100);

	// --- State: column widths (user-resizable) --------------------------
	const [colWidths, setColWidths] = useState<Record<string, number>>({});

	// Initialise colWidths from metadata on first render / when metadata changes
	useEffect(() => {
		const initial: Record<string, number> = {};
		for (const col of columns) {
			initial[col] = metadata.columnWidths[col] || metadata.defaultColWidth || 80;
		}
		setColWidths(initial);
	}, [columns, metadata.columnWidths, metadata.defaultColWidth]);

	// --- Derived data ---------------------------------------------------

	const visibleColumns = useMemo(
		() => columns.filter((c) => !metadata.hiddenColumns?.includes(c)),
		[columns, metadata.hiddenColumns]
	);

	const rowMap = useMemo(() => {
		const m = new Map<number, StyledRow>();
		for (const r of rows) m.set(r.rowIndex, r);
		return m;
	}, [rows]);

	// Build skip-set for non-master merged cells
	const skipCells = useMemo(() => {
		const skip = new Set<string>();
		for (const row of rows) {
			for (const cell of Object.values(row.cells)) {
				if (cell.isMergeMaster && cell.colSpan && cell.rowSpan) {
					const masterIdx = columns.indexOf(cell.col);
					for (let dr = 0; dr < cell.rowSpan; dr++) {
						for (let dc = 0; dc < cell.colSpan; dc++) {
							if (dr === 0 && dc === 0) continue;
							const tCol = columns[masterIdx + dc];
							if (tCol) skip.add(`${row.rowIndex + dr}:${tCol}`);
						}
					}
				}
			}
		}
		return skip;
	}, [rows, columns]);

	const maxRowIndex = useMemo(
		() => (rows.length > 0 ? Math.max(...rows.map((r) => r.rowIndex)) : 0),
		[rows]
	);

	const defaultRowHeight = metadata.defaultRowHeight || 22;

	// --- Column resizing ------------------------------------------------

	const resizingRef = useRef<{
		col: string;
		startX: number;
		startWidth: number;
	} | null>(null);

	const handleResizeStart = useCallback(
		(e: React.MouseEvent, col: string) => {
			e.preventDefault();
			e.stopPropagation();
			const startWidth = colWidths[col] || 80;
			resizingRef.current = { col, startX: e.clientX, startWidth };

			const handleMove = (me: MouseEvent) => {
				if (!resizingRef.current) return;
				const delta = me.clientX - resizingRef.current.startX;
				const resizing = resizingRef.current;
				if (!resizing) return;
				const newWidth = Math.max(30, resizing.startWidth + delta);
				setColWidths((prev) => ({ ...prev, [resizing.col]: newWidth }));
			};

			const handleUp = () => {
				resizingRef.current = null;
				document.removeEventListener('mousemove', handleMove);
				document.removeEventListener('mouseup', handleUp);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
			};

			document.addEventListener('mousemove', handleMove);
			document.addEventListener('mouseup', handleUp);
			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';
		},
		[colWidths]
	);

	// Double-click column border → auto-fit width to content
	const handleResizeDoubleClick = useCallback(
		(col: string) => {
			let maxWidth = 60;
			for (const row of rows) {
				const cell = row.cells[col];
				if (cell?.value != null) {
					const textLen = String(cell.value).length;
					const estimated = Math.min(500, textLen * 8 + 16);
					if (estimated > maxWidth) maxWidth = estimated;
				}
			}
			setColWidths((prev) => ({ ...prev, [col]: maxWidth }));
		},
		[rows]
	);

	// --- Cell interactions -----------------------------------------------

	const handleCellClick = useCallback((rowIndex: number, col: string) => {
		setSelectedCell({ rowIndex, col });
	}, []);

	const handleCellDoubleClick = useCallback(
		(rowIndex: number, col: string) => {
			if (editable) setEditingCell({ rowIndex, col });
		},
		[editable]
	);

	const handleCellSave = useCallback(
		(rowIndex: number, col: string, newValue: string) => {
			setEditingCell(null);
			if (onCellChange) onCellChange(rowIndex, col, newValue);
		},
		[onCellChange]
	);

	const handleCellCancel = useCallback(() => {
		setEditingCell(null);
	}, []);

	// Tab navigation: move to next/previous visible column, wrapping rows
	const handleTab = useCallback(
		(rowIndex: number, col: string, shiftKey: boolean) => {
			const colIdx = visibleColumns.indexOf(col);
			if (shiftKey) {
				if (colIdx > 0) {
					setEditingCell({ rowIndex, col: visibleColumns[colIdx - 1] });
				} else if (rowIndex > 0) {
					setEditingCell({
						rowIndex: rowIndex - 1,
						col: visibleColumns[visibleColumns.length - 1],
					});
				}
			} else {
				if (colIdx < visibleColumns.length - 1) {
					setEditingCell({ rowIndex, col: visibleColumns[colIdx + 1] });
				} else if (rowIndex < maxRowIndex) {
					setEditingCell({ rowIndex: rowIndex + 1, col: visibleColumns[0] });
				}
			}
		},
		[visibleColumns, maxRowIndex]
	);

	// --- Total table width (for proper zoom scaling) ---------------------

	const totalWidth = useMemo(() => {
		let w = 44; // row-number gutter
		for (const col of visibleColumns) {
			w += colWidths[col] || 80;
		}
		return w;
	}, [visibleColumns, colWidths]);

	// =====================================================================
	// Render
	// =====================================================================

	return (
		<Box>
			{/* Zoom controls */}
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				sx={{ mb: 1, justifyContent: 'flex-end' }}>
				<Tooltip title="Zoom out">
					<IconButton
						size="small"
						onClick={() => setZoom((z) => Math.max(50, z - 10))}
						disabled={zoom <= 50}>
						<IconZoomOut fontSize="small" />
					</IconButton>
				</Tooltip>
				<Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>
					{zoom}%
				</Typography>
				<Tooltip title="Zoom in">
					<IconButton
						size="small"
						onClick={() => setZoom((z) => Math.min(200, z + 10))}
						disabled={zoom >= 200}>
						<IconZoomIn fontSize="small" />
					</IconButton>
				</Tooltip>
			</Stack>

			{/* Scrollable container */}
			<Box
				sx={{
					maxHeight,
					overflow: 'auto',
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 1,
					bgcolor: '#fff',
					position: 'relative',
				}}>
				<table
					style={{
						borderCollapse: 'collapse',
						tableLayout: 'fixed',
						width: totalWidth * (zoom / 100),
						transform: `scale(${zoom / 100})`,
						transformOrigin: 'top left',
					}}>
					{/* Column widths */}
					<colgroup>
						<col style={{ width: 44 }} />
						{visibleColumns.map((col) => (
							<col key={col} style={{ width: colWidths[col] || 80 }} />
						))}
					</colgroup>

					{/* Column letter header */}
					<thead>
						<tr style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 3 }}>
							{/* Corner cell */}
							<th
								style={{
									backgroundColor: '#e0e0e0',
									border: '1px solid #bbb',
									padding: '2px 4px',
									fontSize: 10,
									color: '#555',
									fontWeight: 600,
									position: 'sticky',
									left: 0,
									zIndex: 4,
									width: 44,
									minWidth: 44,
								}}>
								&nbsp;
							</th>
							{visibleColumns.map((col) => (
								<th
									key={col}
									style={{
										backgroundColor: '#e0e0e0',
										border: '1px solid #bbb',
										padding: '2px 4px',
										fontSize: 10,
										color: '#555',
										fontWeight: 600,
										textAlign: 'center',
										whiteSpace: 'nowrap',
										position: 'relative',
										userSelect: 'none',
										overflow: 'hidden',
									}}>
									{/* Column letter + optional label */}
									<span title={headerLabels?.[col] ? `${col}: ${headerLabels[col]}` : col}>
										{col}
									</span>

									{/* Resize handle — right edge of each column header */}
									<div
										onMouseDown={(e) => handleResizeStart(e, col)}
										onDoubleClick={() => handleResizeDoubleClick(col)}
										style={{
											position: 'absolute',
											right: 0,
											top: 0,
											bottom: 0,
											width: 5,
											cursor: 'col-resize',
											backgroundColor: 'transparent',
										}}
										onMouseEnter={(e) => {
											(e.target as HTMLDivElement).style.backgroundColor = '#90caf9';
										}}
										onMouseLeave={(e) => {
											(e.target as HTMLDivElement).style.backgroundColor = 'transparent';
										}}
									/>
								</th>
							))}
						</tr>
					</thead>

					<tbody>
						{Array.from({ length: maxRowIndex + 1 }, (_, rowIdx) => {
							const styledRow = rowMap.get(rowIdx);
							if (styledRow?.hidden) return null;

							const rowHeight = styledRow?.height || defaultRowHeight;

							return (
								<tr key={rowIdx} style={{ height: rowHeight }}>
									{/* Row number gutter */}
									<td
										style={{
											backgroundColor: '#e0e0e0',
											border: '1px solid #bbb',
											padding: '2px 4px',
											fontSize: 10,
											color: '#555',
											fontWeight: 600,
											textAlign: 'center',
											position: 'sticky',
											left: 0,
											zIndex: 1,
											width: 44,
											minWidth: 44,
										}}>
										{rowIdx + 1}
									</td>

									{/* Data cells */}
									{visibleColumns.map((col) => {
										if (skipCells.has(`${rowIdx}:${col}`)) return null;

										const cell = styledRow?.cells[col];
										const cs = cellStyleToCSS(cell?.style);

										// Auto-contrast for dark backgrounds
										if (cell?.style?.fill && isDarkColor(cell.style.fill) && !cell?.style?.color) {
											cs.color = '#fff';
										}

										const isEditing = editingCell?.rowIndex === rowIdx && editingCell?.col === col;
										const isSelected =
											selectedCell?.rowIndex === rowIdx && selectedCell?.col === col;

										const tdStyle: CSSProperties = {
											padding: '2px 4px',
											fontSize: '10pt',
											fontFamily: 'Calibri, Arial, sans-serif',
											lineHeight: 1.3,
											maxHeight: rowHeight > 30 ? rowHeight : undefined,
											overflow: 'hidden',
											...cs,
											// Fallback grid lines if the cell has no explicit borders
											...(cs.borderTop || cs.borderBottom || cs.borderLeft || cs.borderRight
												? {}
												: { borderTop: '1px solid #e0e0e0', borderLeft: '1px solid #e0e0e0' }),
											cursor: editable ? 'cell' : 'default',
											position: 'relative',
											outline: isSelected ? '2px solid #1976d2' : undefined,
											outlineOffset: isSelected ? -2 : undefined,
										};

										const displayValue = cell?.value != null ? String(cell.value) : '';

										return (
											<td
												key={col}
												colSpan={cell?.colSpan || 1}
												rowSpan={cell?.rowSpan || 1}
												style={tdStyle}
												onClick={() => handleCellClick(rowIdx, col)}
												onDoubleClick={() => handleCellDoubleClick(rowIdx, col)}
												title={displayValue.length > 30 ? displayValue : undefined}>
												{isEditing ? (
													<EditableCell
														value={displayValue}
														style={cs}
														onSave={(val) => handleCellSave(rowIdx, col, val)}
														onCancel={handleCellCancel}
														onTab={(shift) => handleTab(rowIdx, col, shift)}
													/>
												) : cell?.hyperlink ? (
													<a
														href={cell.hyperlink}
														target="_blank"
														rel="noopener noreferrer"
														style={{ color: cs.color || '#0563C1', textDecoration: 'underline' }}>
														{displayValue}
													</a>
												) : (
													<>{displayValue}</>
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</Box>
		</Box>
	);
};
